import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Alert, Stack, InputAdornment, IconButton, Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import authService from '../../services/authService';

/**
 * Change-password dialog for the logged-in user. Patients log in with the
 * temporary password emailed at creation and change it here. Validates
 * strength client-side (mirrors the backend rules) so most errors surface
 * before the request.
 */
const RULES = [
  { test: (v) => v.length >= 8, label: 'Al menos 8 caracteres' },
  { test: (v) => /[A-Z]/.test(v), label: 'Una mayúscula' },
  { test: (v) => /[a-z]/.test(v), label: 'Una minúscula' },
  { test: (v) => /\d/.test(v), label: 'Un número' },
];

const ChangePasswordDialog = ({ open, onClose }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm('');
    setShow(false); setError(''); setOk(false); setSaving(false);
  };

  React.useEffect(() => { if (open) reset(); }, [open]);

  const unmet = RULES.filter((r) => !r.test(next));
  const canSubmit = current && next && confirm && unmet.length === 0 && next === confirm && !saving;

  const handleSave = async () => {
    setError('');
    if (next !== confirm) { setError('Las contraseñas nuevas no coinciden.'); return; }
    if (unmet.length) { setError('La nueva contraseña no cumple los requisitos.'); return; }
    setSaving(true);
    try {
      await authService.changePassword(current, next, confirm);
      setOk(true);
      setTimeout(() => { onClose(); }, 1400);
    } catch (e) {
      setError(e.message || 'No se pudo cambiar la contraseña.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Cambiar contraseña</DialogTitle>
      <DialogContent dividers>
        {ok ? (
          <Alert severity="success">Contraseña actualizada correctamente.</Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Contraseña actual" type={show ? 'text' : 'password'} fullWidth
              value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow((s) => !s)} edge="end" tabIndex={-1}>
                      {show ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Nueva contraseña" type={show ? 'text' : 'password'} fullWidth
              value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password"
            />
            <TextField
              label="Confirmar nueva contraseña" type={show ? 'text' : 'password'} fullWidth
              value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password"
              error={Boolean(confirm) && confirm !== next}
              helperText={confirm && confirm !== next ? 'No coincide' : ' '}
            />
            <Stack spacing={0.25}>
              {RULES.map((r) => (
                <Typography
                  key={r.label}
                  variant="caption"
                  sx={{ color: r.test(next) ? 'success.main' : 'text.secondary' }}
                >
                  {r.test(next) ? '✓' : '○'} {r.label}
                </Typography>
              ))}
            </Stack>
          </Stack>
        )}
      </DialogContent>
      {!ok && (
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSubmit}>
            {saving ? 'Guardando…' : 'Cambiar'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ChangePasswordDialog;
